import {
  Button,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { BackupSchedule } from '@repo/api-gw-sdk';
import { ScheduleMode } from '@repo/api-gw-sdk';
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';

import { MIN_WINDOW_HOURS } from '../controls/hoursSelector';
import { getFrequencyByWindow } from '../frequency';

export const ScheduleModeSelector = ({
  wizardState,
  setWizardState,
  useUtc,
  setUseUtc,
  timesPerDay,
}: {
  wizardState: BackupPolicyEditState;
  setWizardState: Dispatch<SetStateAction<BackupPolicyEditState>>;
  useUtc: boolean;
  setUseUtc: (useUtc: boolean) => void;
  timesPerDay: number;
}) => {
  const [changeScheduleTypeDialogOpen, setChangeScheduleTypeDialogOpen] =
    useState(false);

  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      {wizardState.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED && (
        <>
          <Typography>Schedules are in</Typography>
          <Select
            MenuProps={{
              disablePortal: true,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },
              sx: {
                marginTop: '-2px',
              },
            }}
            value={useUtc ? 'utc' : 'resource'}
            onChange={(e) => {
              setUseUtc(e.target.value === 'utc');
              setWizardState((state) => ({
                ...state,
                schedules: state.schedules?.map((s) => ({
                  ...s,
                  useUtc: e.target.value === 'utc',
                })),
              }));
            }}
            variant='standard'
            data-testid='create-backup-policy-frequency-select'
          >
            <MenuItem value='resource'>Resource time zone</MenuItem>
            <MenuItem value='utc'>UTC</MenuItem>
          </Select>
          <Divider orientation='vertical' flexItem className='mr-[12px]' />
        </>
      )}
      <Typography>Schedule type</Typography>
      <Button
        sx={{
          padding: '5px 12px',
        }}
        onClick={() => setChangeScheduleTypeDialogOpen(true)}
        data-testid='create-backup-policy-retention-select'
        disableFocusRipple
      >
        <Typography fontWeight={600}>
          {wizardState.mode === ScheduleMode.SCHEDULE_MODE_BASIC
            ? 'Basic'
            : 'Advanced'}
        </Typography>

        <i className='material-symbols-arrow-drop-down-rounded text-[22px] ml-[4px]' />
      </Button>
      <ChangeScheduleTypeDialog
        open={changeScheduleTypeDialogOpen}
        timesPerDay={timesPerDay}
        mode={wizardState.mode}
        onCancel={() => setChangeScheduleTypeDialogOpen(false)}
        onSelect={(mode) => {
          setChangeScheduleTypeDialogOpen(false);
          setUseUtc(false);

          setWizardState((state) => ({
            ...state,
            mode,
            schedules: (state.schedules || []).reduce<BackupSchedule[]>(
              (agg, val) => {
                const frequency = getFrequencyByWindow(val.windows);
                if (frequency) {
                  agg.push({
                    ...val,
                    useUtc: false,
                    windows: frequency.windows,
                    duration: MIN_WINDOW_HOURS * 60,
                    backupRetention: frequency.defaultRetentionInDays,
                  });
                }

                return agg;
              },
              []
            ),
          }));
        }}
      />
    </Stack>
  );
};

const ChangeScheduleTypeDialog = ({
  open,
  mode,
  onCancel,
  onSelect,
  timesPerDay,
}: {
  open: boolean;
  mode: ScheduleMode;
  onCancel: () => void;
  onSelect: (mode: ScheduleMode) => void;
  timesPerDay: number;
}) => {
  const [modeValue, setModeValue] = useState(mode);

  useEffect(() => {
    setModeValue(mode);
  }, [mode]);

  return (
    <MainDialog
      sx={{ minWidth: '720px' }}
      isOpen={open}
      isLoading={false}
      header={{
        title: 'Schedule type',
        onClose: () => onCancel(),
      }}
      footer={{
        primaryButton: {
          'data-testid': 'approve-change-schedule-type',
          text: 'Select',
          onClick: () =>
            mode === modeValue ? onCancel() : onSelect(modeValue),
        },
        secondaryButton: {
          'data-testid': 'cancel-change-schedule-type',
          onClick: () => onCancel(),
          text: 'Cancel',
        },
      }}
    >
      <Stack gap='40px' className='my-[20px]'>
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='16px'>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Basic schedule'
              checked={modeValue === ScheduleMode.SCHEDULE_MODE_BASIC}
              onChange={() => setModeValue(ScheduleMode.SCHEDULE_MODE_BASIC)}
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              You set the frequency, retention, and vault. Eon sets the
              schedule.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack gap='16px'>
            <Typography lineHeight='20px'>
              Eon schedules backups for 00:00 and adjusts the start time range
              automatically.
            </Typography>
            <Stack gap='8px'>
              <Stack direction='row' alignItems='center' gap='12px'>
                <Typography className='w-[110px]'>Daily</Typography>
                <Typography>Every day</Typography>
              </Stack>
              <Stack direction='row' alignItems='center' gap='12px'>
                <Typography className='w-[110px]'>Weekly</Typography>
                <Typography>Every Sunday</Typography>
              </Stack>
              <Stack direction='row' alignItems='center' gap='12px'>
                <Typography className='w-[110px]'>Monthly</Typography>
                <Typography>Every first of the month</Typography>
              </Stack>
              <Stack direction='row' alignItems='center' gap='12px'>
                <Typography className='w-[110px]'>Annually</Typography>
                <Typography>Every January 1</Typography>
              </Stack>
              <Divider sx={{ borderStyle: 'dashed', marginY: '4px' }} />
              <Stack direction='row' alignItems='center' gap='12px'>
                <Typography className='w-[110px]'>More frequently</Typography>
                <Typography>{`Up to ${timesPerDay} times a day`}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='12px'>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Advanced schedule'
              checked={modeValue === ScheduleMode.SCHEDULE_MODE_ADVANCED}
              onChange={() => setModeValue(ScheduleMode.SCHEDULE_MODE_ADVANCED)}
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              Tailor the schedule to your needs.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack gap='4px'>
            <List sx={{ listStyle: 'disc', pl: 4 }}>
              <ListItem sx={{ display: 'list-item', padding: '0 0 4px 0' }}>
                <ListItemText primary='Customize backup start time range.' />
              </ListItem>
              <ListItem sx={{ display: 'list-item', padding: '0 0 4px 0' }}>
                <ListItemText primary='Change schedule time zone.' />
              </ListItem>
              <ListItem sx={{ display: 'list-item', padding: '0 0 4px 0' }}>
                <ListItemText primary='Set one or more backups per week, month, or year.' />
              </ListItem>
              <ListItem sx={{ display: 'list-item', padding: 0 }}>
                <ListItemText primary='Choose specific days or dates.' />
              </ListItem>
            </List>
          </Stack>
        </Stack>
      </Stack>
    </MainDialog>
  );
};
